import { useState, useEffect, createContext, useContext } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  const loadUserProfile = async (userId, userEmail) => {
    try {
      // TODO: Load from Firestore user profile
      // For now, using dummy data
      const isAdminUser = userEmail === 'admin@escape.com';
      const dummyProfile = {
        subscriptionStatus: isAdminUser ? 'premium' : 'free', // free, paid, premium
        isPremium: isAdminUser || Math.random() > 0.7, // Randomly assign premium for demo
        isAdmin: isAdminUser,
        hasActivePaidAccess: isAdminUser || Math.random() > 0.5, // Randomly assign paid access for demo
        subscriptionEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
        stripeCustomerId: `cus_dummy_${userId}`,
        subscriptionId: `sub_dummy_${userId}`
      };
      
      setUserProfile(dummyProfile);
      console.log('👤 User profile loaded:', dummyProfile);
      return dummyProfile;
    } catch (error) {
      console.error('Failed to load user profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadUserProfile(user.uid, user.email);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const updateUserSubscription = async (subscriptionData) => {
    if (userProfile) {
      const updatedProfile = {
        ...userProfile,
        ...subscriptionData
      };
      setUserProfile(updatedProfile);
      console.log('💳 User subscription updated:', updatedProfile);
      // TODO: Update in Firestore
    }
  };

  const signup = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    userProfile,
    signup,
    login,
    logout,
    loading,
    updateUserSubscription,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
